import type { CurrencyCode, IsoDateTime, PostalAddress } from './common';
import type { CredentialId, OrderId, OrderItemId, ProductId, TenantId, WarehouseId } from './ids';
import type { MarketplaceChannel } from './marketplace';
import type { CarrierCode, ShippingModel } from './shipping';

export const ORDER_STATUSES = [
  'pending',
  'backordered',
  'on_hold',
  'allocated',
  'picking',
  'picked',
  'packing',
  'packed',
  'shipped',
  'delivered',
  'cancelled',
  'returned',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** As shown to warehouse staff and sellers */
export const ORDER_STATUS_LABELS: Readonly<Record<OrderStatus, string>> = {
  pending: 'Rezerve bekliyor',
  backordered: 'Stok bekliyor',
  on_hold: 'Beklemede',
  allocated: 'Toplamaya hazır',
  picking: 'Toplanıyor',
  picked: 'Paketlemeye hazır',
  packing: 'Paketleniyor',
  packed: 'Kargoya hazır',
  shipped: 'Kargoda',
  delivered: 'Teslim edildi',
  cancelled: 'İptal',
  returned: 'İade',
};

/**
 * Allowed status changes. PostgreSQL enforces the same graph in
 * app.order_status_transition_allowed(); a test keeps both identical.
 */
export const ORDER_STATUS_TRANSITIONS = {
  pending: ['allocated', 'backordered', 'on_hold', 'cancelled'],
  backordered: ['allocated', 'on_hold', 'cancelled'],
  on_hold: ['pending', 'cancelled'],
  allocated: ['picking', 'pending', 'on_hold', 'cancelled'],
  picking: ['picked', 'allocated', 'cancelled'],
  // picking again: the pick of its last stop was undone
  picked: ['packing', 'picking', 'cancelled'],
  // on hold: a unit missing or broken at the desk
  packing: ['packed', 'picked', 'on_hold', 'cancelled'],
  // packing again: geri al at the desk while the parcel is still there
  packed: ['shipped', 'packing', 'cancelled'],
  // packed again: the handover was taken back (the courier did not take the parcel)
  shipped: ['delivered', 'returned', 'packed'],
  delivered: ['returned'],
  cancelled: [],
  // shipped / delivered again: the return that completed the order was taken back at the desk
  returned: ['shipped', 'delivered'],
} as const satisfies Record<OrderStatus, readonly OrderStatus[]>;

/** Cancellations the system makes itself; a person's reason is shown as it was typed. */
export const SYSTEM_CANCEL_REASONS: Readonly<Record<string, string>> = {
  channel_cancelled: 'Pazaryerinde iptal edildi',
  channel_cancelled_after_packing: 'Paketlendikten sonra pazaryerinde iptal edildi',
  channel_shipped_elsewhere: 'Satıcı kendisi gönderdi (pazaryerinde kargoda göründü)',
  // POST /api/v1/orders/{orderNumber}/cancel without a reason (the shop refunded the order)
  seller_system_cancelled: 'Satıcının kendi sisteminden iptal edildi',
};

export function cancelReasonText(reason: string | null | undefined): string | null {
  return reason ? (SYSTEM_CANCEL_REASONS[reason] ?? reason) : null;
}

/**
 * A parcel packed and then cancelled before it left (KARGOYA VERMEYİN on Sevkiyat): listed while its
 * units wait to be put back, which takes opening it.
 */
export interface CancelledParcel {
  readonly orderId: OrderId;
  readonly orderNumber: string;
  /** the marketplace the order came from */
  readonly channel: MarketplaceChannel;
  readonly tenantCode: string;
  readonly carrierCode: string | null;
  readonly trackingNumber: string | null;
  /** labels printed before the marketplace moved the parcel to another carrier */
  readonly supersededTracking: readonly string[];
  /** a code of SYSTEM_CANCEL_REASONS, or what the person who cancelled wrote */
  readonly cancelReason: string | null;
  readonly cancelledAt: IsoDateTime;
  /** units still on their way back to the shelf (Yerleştirme → Rafa iade) */
  readonly units: number;
}

/** The dock's answer to the label of a cancelled order (after "KARGOYA VERMEYİN:"); putBack: its units wait in Rafa iade. */
export function doNotShipText(putBack: boolean): string {
  return putBack
    ? 'Bu sipariş iptal edildi. Paketi açın; ürünler Yerleştirme → Rafa iade görevinde.'
    : 'Bu sipariş iptal edildi. Paketi kuryeye vermeyin, vardiya sorumlusuna gösterin.';
}

/** A line the marketplace cancelled while the rest of the order stands: it no longer goes with the order. */
export interface CancelledLine {
  readonly orderItemId: OrderItemId;
  readonly sku: string | null;
  readonly name: string;
  /** units of the warehouse (a "2'li set" line counts its pieces) */
  readonly qty: number;
  readonly cancelledAt: IsoDateTime;
}

/**
 * A closed parcel a line of which the marketplace cancelled before it left (migration 0062): the
 * order went back to packing, Sevkiyat refuses its label and lists it until a desk packs it again
 * without the cancelled goods (KARGOYA VERMEYİN · yeniden paketlenecek).
 */
export interface RepackParcel {
  readonly orderId: OrderId;
  readonly orderNumber: string;
  /** the marketplace the order came from */
  readonly channel: MarketplaceChannel;
  readonly tenantCode: string;
  readonly carrierCode: string | null;
  readonly trackingNumber: string | null;
  /** labels printed before the marketplace moved the parcel to another carrier */
  readonly supersededTracking: readonly string[];
  /** when the parcel was opened for the cancelled line */
  readonly since: IsoDateTime;
  /** what comes out of the parcel */
  readonly cancelledLines: readonly CancelledLine[];
}

/** "MUG-1 × 2, BIG-1 × 1" */
export function cancelledLinesText(lines: readonly Pick<CancelledLine, 'sku' | 'name' | 'qty'>[]): string {
  return lines.map((l) => `${l.sku ?? l.name} × ${l.qty}`).join(', ');
}

/** The dock's answer to the label of a parcel to be packed again (after "KARGOYA VERMEYİN:"). */
export function repackText(lines: readonly Pick<CancelledLine, 'sku' | 'name' | 'qty'>[]): string {
  const what = lines.length > 0 ? `${cancelledLinesText(lines)} iptal edildi` : 'Siparişin bir ürünü iptal edildi';
  return `Paketlendikten sonra ${what}. Paketi masaya götürün: açın, iptal edilen ürünü çıkarıp Yerleştirme → Rafa iade ile rafına koyun, kalanını yeniden paketleyin.`;
}

/** Sellers may cancel only before picking starts; after that the warehouse has to unwind the order. */
export const SELLER_CANCELLABLE_STATUSES = ['pending', 'backordered', 'on_hold', 'allocated'] as const satisfies readonly OrderStatus[];

export function canTransitionOrder(from: OrderStatus, to: OrderStatus): boolean {
  const next: readonly OrderStatus[] = ORDER_STATUS_TRANSITIONS[from];
  return next.includes(to);
}

/** One fulfillable package. A marketplace order split into several packages becomes several rows. */
export interface Order {
  readonly id: OrderId;
  readonly tenantId: TenantId;
  readonly warehouseId: WarehouseId;
  readonly channel: MarketplaceChannel;
  /** null for manual orders typed in by the seller */
  readonly credentialId: CredentialId | null;
  readonly externalOrderId: string;
  /** Trendyol shipmentPackageId, Hepsiburada package number; null where the channel has no packages */
  readonly externalPackageId: string | null;
  readonly orderNumber: string;
  readonly status: OrderStatus;
  /** Raw status reported by the channel, for diagnostics */
  readonly channelStatus: string | null;
  readonly holdReason: string | null;
  readonly priority: number;
  readonly orderedAt: IsoDateTime;
  /** Latest hand-over time to the carrier promised to the marketplace */
  readonly shipBy: IsoDateTime | null;
  readonly customerName: string | null;
  readonly customerEmail: string | null;
  readonly customerPhone: string | null;
  readonly shippingAddress: PostalAddress;
  readonly billingAddress: PostalAddress | null;
  readonly currency: CurrencyCode;
  /** numeric(14,2) as a decimal string */
  readonly totalAmount: string | null;
  readonly shippingModel: ShippingModel;
  readonly carrierCode: CarrierCode | null;
  readonly trackingNumber: string | null;
  readonly sellerNote: string | null;
  readonly cancelReason: string | null;
  readonly packedAt: IsoDateTime | null;
  readonly shippedAt: IsoDateTime | null;
  readonly cancelledAt: IsoDateTime | null;
  readonly createdAt: IsoDateTime;
  readonly updatedAt: IsoDateTime;
}

export interface OrderItem {
  readonly id: OrderItemId;
  readonly tenantId: TenantId;
  readonly orderId: OrderId;
  readonly lineNo: number;
  /** null until the channel SKU/barcode is matched to a product; the order waits on_hold meanwhile */
  readonly productId: ProductId | null;
  /** Trendyol lineId, Amazon OrderItemId, Shopify line item id: needed to report status per line */
  readonly externalLineId: string | null;
  readonly sku: string | null;
  readonly barcode: string | null;
  readonly productName: string;
  readonly qtyOrdered: number;
  /** Maintained by PostgreSQL from inventory_allocations */
  readonly qtyAllocated: number;
  readonly qtyPicked: number;
  /** Advanced by the packing station as items are scanned */
  readonly qtyPacked: number;
  /** numeric(14,2) as a decimal string */
  readonly unitPrice: string | null;
  /** KDV % as a decimal string */
  readonly vatRate: string | null;
}
